import { readFileSync, existsSync, copyFileSync } from 'node:fs'
import { homedir } from 'node:os'
import { join } from 'node:path'
import writeFile from 'write-file-atomic'

interface McpServerEntry {
  command: string
  args?: string[]
  env?: Record<string, string>
}

interface ClaudeJson {
  mcpServers?: Record<string, McpServerEntry>
  [key: string]: unknown
}

function readJsonFile(filePath: string): ClaudeJson {
  if (!existsSync(filePath)) {
    return {}
  }
  try {
    return JSON.parse(readFileSync(filePath, 'utf8')) as ClaudeJson
  } catch {
    // File exists but is corrupt — back it up and start fresh
    const backupPath = `${filePath}.bak`
    copyFileSync(filePath, backupPath)
    return {}
  }
}

/**
 * Detects and removes standalone jcodemunch MCP entries (uvx-based or jcodemunch-mcp entries).
 * Logs a warning if a standalone entry is replaced.
 */
function removeStandaloneEntries(config: ClaudeJson): void {
  if (!config.mcpServers) return

  const standaloneKeys = ['jcodemunch-mcp', 'jcodemunch']
  for (const key of standaloneKeys) {
    const entry = config.mcpServers[key]
    if (entry) {
      const isStandalone =
        entry.command?.includes('uvx') ||
        entry.args?.some(arg => arg.includes('jcodemunch-mcp') || arg.includes('uvx'))
      if (isStandalone) {
        console.warn('gsd-munch: Replacing standalone jcodemunch MCP entry with bundled version.')
        delete config.mcpServers[key]
      }
    }
  }
}

/**
 * Registers jcodemunch in the user-scoped ~/.claude.json config using venv Python.
 * Reads the existing file, detects and replaces standalone entries, merges the
 * bundled venv-based entry, and writes atomically.
 * Preserves all other top-level keys in ~/.claude.json.
 *
 * @param venvPythonPath Absolute path to the venv Python binary
 * @param pythonPath Absolute path to the python/ directory (used as PYTHONPATH)
 */
export async function registerMcpUserScope(venvPythonPath: string, pythonPath: string): Promise<void> {
  const claudeJsonPath = join(homedir(), '.claude.json')

  const config = readJsonFile(claudeJsonPath)

  removeStandaloneEntries(config)

  config.mcpServers = {
    ...(config.mcpServers ?? {}),
    jcodemunch: {
      command: venvPythonPath,
      args: ['-m', 'jcodemunch_mcp.server'],
      env: {
        PYTHONPATH: pythonPath,
        JCODEMUNCH_USE_AI_SUMMARIES: 'false',
      },
    },
  }

  await writeFile(claudeJsonPath, JSON.stringify(config, null, 2) + '\n', 'utf8')
}

/**
 * Registers jcodemunch in a project-scoped .mcp.json file in the current working directory.
 * Reads the existing file, detects and replaces standalone entries, merges the
 * bundled venv-based entry, and writes atomically.
 * Preserves all other server entries.
 *
 * @param venvPythonPath Absolute path to the venv Python binary
 * @param pythonPath Absolute path to the python/ directory (used as PYTHONPATH)
 */
export async function registerMcpProjectScope(venvPythonPath: string, pythonPath: string): Promise<void> {
  const mcpJsonPath = join(process.cwd(), '.mcp.json')

  const config = readJsonFile(mcpJsonPath)

  removeStandaloneEntries(config)

  config.mcpServers = {
    ...(config.mcpServers ?? {}),
    jcodemunch: {
      command: venvPythonPath,
      args: ['-m', 'jcodemunch_mcp.server'],
      env: {
        PYTHONPATH: pythonPath,
        JCODEMUNCH_USE_AI_SUMMARIES: 'false',
      },
    },
  }

  await writeFile(mcpJsonPath, JSON.stringify(config, null, 2) + '\n', 'utf8')
}

/**
 * Fast file-based check for MCP registration status.
 * Does not spawn any processes — suitable for the status screen (<100ms).
 *
 * @returns Object indicating whether jcodemunch is registered at user and project scope
 */
export function getMcpRegistrationStatus(): { userScope: boolean; projectScope: boolean } {
  let userScope = false
  let projectScope = false

  try {
    const claudeJsonPath = join(homedir(), '.claude.json')
    if (existsSync(claudeJsonPath)) {
      const config = JSON.parse(readFileSync(claudeJsonPath, 'utf8')) as ClaudeJson
      userScope = Boolean(config.mcpServers?.jcodemunch)
    }
  } catch {
    userScope = false
  }

  try {
    const mcpJsonPath = join(process.cwd(), '.mcp.json')
    if (existsSync(mcpJsonPath)) {
      const config = JSON.parse(readFileSync(mcpJsonPath, 'utf8')) as ClaudeJson
      projectScope = Boolean(config.mcpServers?.jcodemunch)
    }
  } catch {
    projectScope = false
  }

  return { userScope, projectScope }
}
